/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from "react";
import { Users, Search, X, Crown, Shield, Headphones, Eye, UserCircle, MoreVertical } from "lucide-react";

// Lightweight panel — receives participants and role-aware action callbacks
// from Room.jsx. Keeps the source of truth in the parent (LiveKit + roomMeta).
//
// Each participant prop shape: {
//   identity, name, joined_at, role: "host"|"cohost"|"dj"|"guest"|"listener",
//   isMe?: bool, videoEl?: HTMLVideoElement (optional thumb source — unused for now)
// }
export default function ParticipantsPanel({ participants = [], myIdentity, open, onClose, onOpenDM, onAction, moderationRole = "none" }) {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return participants;
    return participants.filter((p) => (p.name || "").toLowerCase().includes(needle) || (p.role || "").toLowerCase().includes(needle));
  }, [q, participants]);

  const counts = useMemo(() => {
    const out = { total: participants.length, staff: 0, participants: 0 };
    participants.forEach((p) => {
          const roles = p.roles?.length ? p.roles : [p.role];
          if (roles.includes("host") || roles.includes("cohost") || roles.includes("dj")) out.staff += 1;
          else out.participants += 1;
    });
    return out;
  }, [participants]);

  if (!open) return null;

  return (
    <>
      {/* Phase B — mobile: tap-outside-to-close backdrop. Hidden on md+. */}
      <div className="md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="participants-panel-backdrop" />
      <aside
        className="md:w-[300px] md:shrink-0 md:h-full md:border-l md:border-white/10 md:relative md:inset-auto md:z-auto fixed inset-0 z-50 w-full h-[100dvh] bg-[#08080c] flex flex-col"
        data-testid="participants-panel">
      <header className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
        <Users className="w-4 h-4 text-[#00F0FF]" />
        <h3 className="text-sm font-semibold">Participants</h3>
        <span className="text-[10px] text-white/40 font-mono ml-1" data-testid="participants-count-pill">{counts.total} · {counts.staff} staff · {counts.participants} participants</span>
        <button onClick={onClose} className="ml-auto min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 md:p-1.5 p-2 rounded hover:bg-white/5 flex items-center justify-center" data-testid="participants-close-btn">
          <X className="w-5 h-5 md:w-4 md:h-4 text-white/50" />
        </button>
      </header>

      <div className="px-3 py-2 border-b border-white/5 flex items-center gap-2">
        <Search className="w-3.5 h-3.5 text-white/40 shrink-0" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Filter by name or role…"
               className="flex-1 bg-transparent text-[12px] text-white placeholder-white/30 focus:outline-none"
               data-testid="participants-search" />
        {q && <button onClick={() => setQ("")} className="text-white/40 hover:text-white" data-testid="participants-search-clear"><X className="w-3 h-3" /></button>}
      </div>

      <div className="flex-1 overflow-y-auto" data-testid="participants-list">
        {filtered.length === 0 ? (
          <div className="text-center text-white/40 text-xs py-8" data-testid="participants-empty">No matches.</div>
        ) : filtered.map((p) => (
          <Row key={p.identity} p={p} myIdentity={myIdentity}
               onOpenDM={onOpenDM}
               moderationRole={moderationRole}
               onAction={onAction} />
        ))}
      </div>
    </aside>
    </>
  );
}

function roleBadge(role) {
  const map = {
    host: { label: "Host", icon: Crown, color: "text-amber-300", bg: "bg-amber-500/15 border-amber-500/30" },
    cohost: { label: "Cohost", icon: Shield, color: "text-[#A78BFA]", bg: "bg-[#7C3AED]/15 border-[#7C3AED]/30" },
    dj: { label: "DJ", icon: Headphones, color: "text-[#FF007F]", bg: "bg-[#FF007F]/15 border-[#FF007F]/30" },
    guest: { label: "Guest", icon: UserCircle, color: "text-white/60", bg: "bg-white/5 border-white/10" },
    listener: { label: "Listener", icon: Eye, color: "text-white/55", bg: "bg-white/5 border-white/10" },
  };
  return map[role] || map.listener;
}

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
  } catch { return ""; }
}

function avatarColor(identity) {
  let h = 0; for (let i = 0; i < (identity || "").length; i++) h = (h * 31 + identity.charCodeAt(i)) & 0xffff;
  return `hsl(${h % 360}, 70%, 50%)`;
}

function Row({ p, myIdentity, onOpenDM, moderationRole, onAction }) {
  const [menu, setMenu] = useState(false);
  const roles = p.roles?.length ? p.roles : [p.role];
  const isMe = p.identity === myIdentity;
  const isStaffTarget = roles.includes("host") || roles.includes("cohost");
  const canMoveOrRemove = (moderationRole === "host" || moderationRole === "cohost") && !isStaffTarget;
  const canAdminister = moderationRole === "host";
  return (
    <div className="group flex items-center gap-2 px-3 py-2 hover:bg-white/[0.04] relative" data-testid={`participants-row-${p.identity}`}>
      <div className="w-8 h-8 rounded-full shrink-0 grid place-items-center text-[11px] font-bold uppercase"
           style={{ background: avatarColor(p.identity) }}>
        {(p.name || "?").slice(0, 1)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold text-white/90 truncate flex items-center gap-1">
          {p.name || p.identity?.slice(0, 8)}
          {isMe && <span className="text-[9px] text-white/40">(you)</span>}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          {roles.map((role) => {
            const badge = roleBadge(role);
            const RoleIcon = badge.icon;
            return (
              <span key={role} className={`px-1.5 py-0.5 text-[9px] uppercase font-bold rounded border ${badge.bg} ${badge.color} flex items-center gap-0.5`}
                    data-testid={`participants-row-role-${p.identity}-${role}`}>
                <RoleIcon className="w-2.5 h-2.5" /> {badge.label}
              </span>
            );
          })}
          {p.joined_at && <span className="text-[9px] text-white/35 font-mono" data-testid={`participants-row-time-${p.identity}`}>{timeAgo(p.joined_at)}</span>}
        </div>
      </div>
      {!isMe && (
        <button onClick={() => setMenu((v) => !v)}
                className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-1 rounded hover:bg-white/10"
                data-testid={`participants-row-menu-${p.identity}`}>
          <MoreVertical className="w-3.5 h-3.5 text-white/50" />
        </button>
      )}
      {menu && (
        <div className="absolute right-2 top-10 z-30 bg-[#0d0d14] border border-white/10 rounded-lg shadow-2xl py-1 min-w-[140px]" data-testid={`participants-row-menu-pop-${p.identity}`}>
          <button onClick={() => { setMenu(false); onOpenDM?.(p.identity, p.name); }}
                  className="w-full px-3 py-1.5 text-left text-[12px] hover:bg-white/5"
                  data-testid={`participants-row-dm-${p.identity}`}>💬 Chat (DM)</button>
          {canAdminister && (
            <>
              {!roles.includes("cohost") && !roles.includes("host") && (
                <button onClick={() => { setMenu(false); onAction?.("make_cohost", p); }}
                        className="w-full px-3 py-1.5 text-left text-[12px] hover:bg-white/5">🛡️ Make cohost</button>
              )}
              {roles.includes("cohost") && (
                <>
                  <button onClick={() => { setMenu(false); onAction?.("transfer_host", p); }}
                          className="w-full px-3 py-1.5 text-left text-[12px] hover:bg-white/5">👑 Make host</button>
                  <button onClick={() => { setMenu(false); onAction?.("revoke_cohost", p); }}
                          className="w-full px-3 py-1.5 text-left text-[12px] hover:bg-white/5">🛡️ Revoke cohost</button>
                </>
              )}
              {!roles.includes("dj") && !roles.includes("host") && (
                <button onClick={() => { setMenu(false); onAction?.("make_dj", p); }}
                        className="w-full px-3 py-1.5 text-left text-[12px] hover:bg-white/5">🎧 Make DJ</button>
              )}
              {roles.includes("dj") && !roles.includes("host") && (
                <button onClick={() => { setMenu(false); onAction?.("revoke_dj", p); }}
                        className="w-full px-3 py-1.5 text-left text-[12px] hover:bg-white/5">🎧 End DJ role</button>
              )}
            </>
          )}
          {canMoveOrRemove && (
            <>
              <button onClick={() => { setMenu(false); onAction?.("kick", p); }}
                      className="w-full px-3 py-1.5 text-left text-[12px] hover:bg-white/5">⏸️ Place in waiting room</button>
              <button onClick={() => { setMenu(false); onAction?.("remove", p); }}
                      className="w-full px-3 py-1.5 text-left text-[12px] text-rose-300 hover:bg-rose-500/10">🚪 Remove from room</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Trigger pill — caller renders this where the participants toggle should live.
export function ParticipantsTrigger({ open, onToggle, count }) {
  return (
    <button onClick={onToggle}
            className={`relative px-3 py-2 rounded-lg border text-xs font-semibold flex items-center gap-1.5 transition-colors ${open ? "bg-white/10 border-white/20" : "bg-white/5 border-white/10 hover:bg-white/10"}`}
            data-testid="participants-trigger-btn"
            title="Participants">
      <Users className="w-4 h-4 text-[#00F0FF]" />
      {typeof count === "number" && <span className="text-[10px] font-mono text-white/60" data-testid="participants-trigger-count">{count}</span>}
    </button>
  );
}
